import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  HomeIcon,
  UserCircleIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  PowerIcon,
  ChatBubbleLeftRightIcon,
  WrenchIcon,
  DocumentTextIcon,
  CreditCardIcon,
} from '@heroicons/react/24/solid';
import {
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon
} from '@heroicons/react/24/outline';
import BouteeLogo from './Boutee_Logo_Icon.jpg';

const Sidebar = ({ isCollapsed, toggleCollapse }) => {
  const { pathname } = useLocation();
  const { signOut, currentUser } = useAuth();
  const [newEnquiriesCount, setNewEnquiriesCount] = useState(0);

  const isActive = (path) => pathname === path || pathname.startsWith(`${path}/`);

  // Listen for new enquiries (same logic as Navbar)
  useEffect(() => {
    if (!currentUser?.uid) return;

    const projectsRef = collection(db, 'projects');
    const q = query(
      projectsRef, 
      where('jewellerId', '==', currentUser.uid),
      where('status', '==', 'new_enquiry')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setNewEnquiriesCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  const navItems = [
    { path: '/', name: 'Dashboard', icon: HomeIcon },
    { path: '/profile', name: 'Profile', icon: UserCircleIcon },
    { path: '/products', name: 'Products', icon: ShoppingBagIcon },
    { path: '/enquiries', name: 'Enquiries', icon: ChatBubbleLeftRightIcon },
    { path: '/projects', name: 'Projects', icon: WrenchIcon },
    { path: '/payments', name: 'Payments', icon: CreditCardIcon },
    { path: '/community', name: 'Community', icon: UserGroupIcon },
    { path: '/faq', name: 'FAQ', icon: DocumentTextIcon },
  ];

  return (
    <aside 
      className="fixed left-0 top-0 h-screen z-40 transition-all duration-300 ease-in-out"
      style={{
        backgroundColor: 'var(--Surface-Primary)',
        borderRight: '1px solid var(--Stroke-Light)',
        boxShadow: 'var(--shadow-frame-big)',
        width: isCollapsed ? '80px' : '264px',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div style={{
          borderBottom: '1px solid var(--Stroke-Light)',
          padding: isCollapsed ? 'var(--spacing-m)' : 'var(--spacing-xl)',
          backgroundColor: 'var(--Surface-Secondary)'
        }}>
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}
              style={{ gap: 'var(--spacing-s)' }}
            >
              {/* Logo/Icon */}
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-s)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                overflow: 'hidden'
              }}>
                <img 
                  src={BouteeLogo} 
                  alt="Boutee Logo" 
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
              </div>
              
              {/* Text (hidden when collapsed) */}
              {!isCollapsed && (
                <div className="overflow-hidden">
                  <h2 style={{
                    fontSize: 'var(--font-size-subtitle2)',
                    fontWeight: 'var(--font-weight-subtitle2)',
                    color: 'var(--Text-Primary)',
                    lineHeight: 'var(--line-height-subtitle2)'
                  }}>
                    Boutee
                  </h2>
                  <p className="truncate" style={{
                    fontSize: 'var(--font-size-body5)',
                    color: 'var(--Text-Tertiary)',
                    lineHeight: 'var(--line-height-body5)'
                  }}>
                    Jeweller Portal
                  </p>
                </div>
              )}
            </div>
            
            {/* Collapse button - only show on desktop */}
            <button
              onClick={toggleCollapse}
              className={`hidden lg:flex items-center justify-center ${isCollapsed ? '' : 'ml-2'}`}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: 'var(--radius-xs)',
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s ease',
                cursor: 'pointer',
                border: 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--Grey-100)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isCollapsed ? (
                <ChevronDoubleRightIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
              ) : (
                <ChevronDoubleLeftIcon className="w-4 h-4" style={{ color: 'var(--Text-Secondary)' }} />
              )}
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto" style={{ padding: 'var(--spacing-m) var(--spacing-s)' }}>
          <ul style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xxs)' }}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              return (
                <li key={item.path} style={{ position: 'relative' }}>
                  {item.comingSoon ? (
                    // Disabled link for coming soon items
                    <div
                      className="group flex items-center transition-all duration-200"
                      style={{
                        borderRadius: 'var(--radius-s)',
                        padding: isCollapsed ? 'var(--spacing-s)' : 'var(--spacing-s) var(--spacing-m)',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        background: 'transparent',
                        textDecoration: 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'not-allowed'
                      }}
                      title={isCollapsed ? `${item.name} (Coming Soon)` : ''}
                    >
                      {/* Icon */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: 'var(--Grey-400)',
                        opacity: 0.6
                      }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      
                      {/* Text and coming soon badge (hidden when collapsed) */}
                      {!isCollapsed && (
                        <>
                          <span style={{
                            marginLeft: 'var(--spacing-s)',
                            fontSize: 'var(--font-size-body3)',
                            fontWeight: 'var(--font-weight-normal)',
                            flex: 1,
                            color: 'var(--Grey-400)',
                            opacity: 0.6
                          }}>
                            {item.name}
                          </span>
                          
                          <span style={{
                            padding: '2px var(--spacing-xs)',
                            backgroundColor: 'var(--Purple-100)',
                            color: 'var(--Purple-800)',
                            borderRadius: 'var(--radius-pill)',
                            fontSize: '10px',
                            fontWeight: 'var(--font-weight-semibold)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Soon
                          </span>
                        </>
                      )}
                    </div>
                  ) : (
                    // Regular clickable link
                    <Link
                      to={item.path}
                      className="group flex items-center transition-all duration-200"
                      style={{
                        borderRadius: 'var(--radius-s)',
                        padding: isCollapsed ? 'var(--spacing-s)' : 'var(--spacing-s) var(--spacing-m)',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        background: active ? 'var(--Purple-50)' : 'transparent',
                        color: active ? 'var(--Purple-800)' : 'var(--Text-Secondary)',
                        textDecoration: 'none',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'var(--Surface-Secondary)';
                          e.currentTarget.style.color = 'var(--Text-Primary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!active) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.color = 'var(--Text-Secondary)';
                        }
                      }}
                      title={isCollapsed ? item.name : ''}
                    >
                      {/* Icon */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        color: active ? 'var(--Purple-700)' : 'inherit',
                        transition: 'color 0.2s ease',
                        position: 'relative'
                      }}>
                        <Icon className="w-5 h-5" />
                        {/* Red dot for collapsed sidebar */}
                        {isCollapsed && item.path === '/enquiries' && newEnquiriesCount > 0 && (
                          <span style={{
                            position: 'absolute',
                            top: '-4px',
                            right: '-4px',
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'var(--Red-600)',
                            borderRadius: '50%',
                            border: '2px solid var(--Surface-Primary)'
                          }} />
                        )}
                      </div>
                      
                      {/* Text and badge (hidden when collapsed) */}
                      {!isCollapsed && (
                        <>
                          <span style={{
                            marginLeft: 'var(--spacing-s)',
                            fontSize: 'var(--font-size-body3)',
                            fontWeight: active ? 'var(--font-weight-medium)' : 'var(--font-weight-normal)',
                            flex: 1,
                            color: active ? 'var(--Purple-700)' : 'inherit'
                          }}>
                            {item.name}
                          </span>
                          
                          {/* Notification dot for Enquiries */}
                          {item.path === '/enquiries' && newEnquiriesCount > 0 && (
                            <span style={{
                              minWidth: '20px',
                              height: '20px',
                              backgroundColor: 'var(--Red-600)',
                              color: 'var(--Text-Contrast)',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 'var(--font-weight-semibold)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              marginLeft: 'var(--spacing-xs)'
                            }}>
                              {newEnquiriesCount}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer / Sign out */}
        <div style={{
          borderTop: '1px solid var(--Stroke-Light)',
          padding: 'var(--spacing-s)',
          paddingBottom: 'calc(var(--spacing-s) + 48px)', // Extra padding for feedback banner
          backgroundColor: 'var(--Surface-Secondary)'
        }}>
          <button
            onClick={signOut}
            className="w-full flex items-center transition-all duration-200 group"
            style={{
              borderRadius: 'var(--radius-s)',
              padding: isCollapsed ? 'var(--spacing-s)' : 'var(--spacing-s) var(--spacing-m)',
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              backgroundColor: 'transparent',
              color: 'var(--Text-Secondary)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--Red-50)';
              e.currentTarget.style.color = 'var(--Red-600)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--Text-Secondary)';
            }}
            title={isCollapsed ? 'Sign Out' : ''}
          >
            <PowerIcon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && (
              <span style={{
                marginLeft: 'var(--spacing-s)',
                fontSize: 'var(--font-size-body3)',
                fontWeight: 'var(--font-weight-medium)'
              }}>
                Sign Out
              </span>
            )}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
